import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Breadcrumbs, Typography } from "@mui/material";
import { NavigateNextOutlined } from "@mui/icons-material";
import { useSite } from "../../context/siteContext";
import { ROUTES } from "../../constants";
import { DEFAULT_LAYOUT } from "../../config";
import { getMachinesByIds, getSingleProduct, getOrderByKey } from "../../services/breadCrumbsService";
import { useTranslation } from "react-i18next";

const getProductNameFromResponse = (response) => {
  const product =
    response?.data?.data?.[0] ||
    response?.data?.[0] ||
    response?.data;
  return product?.product_name || "";
};

const BreadCrumbs = () => {
  const location = useLocation();
  const pathnames = location.pathname.split("/").filter((x) => x);
  const searchParams = new URLSearchParams(location.search);
  const selectedSiteLineId = searchParams.get("site_line_id") || "";
  const selectedLineName = searchParams.get("line_name") || "";
  const selectedLineNumber = searchParams.get("line_number") || "";
  const machineLineQuery = selectedSiteLineId
    ? `?site_line_id=${encodeURIComponent(selectedSiteLineId)}${selectedLineName ? `&line_name=${encodeURIComponent(selectedLineName)}` : ""}${selectedLineNumber ? `&line_number=${encodeURIComponent(selectedLineNumber)}` : ""}`
    : "";
  const { t } = useTranslation();

  // State to hold fetched names
  const [machineName, setMachineName] = useState(null);
  const [productName, setProductName] = useState(null);
  const [orderName, setOrderName] = useState(null);
  const { selectedSite } = useSite();

  useEffect(() => {
    const fetchData = async () => {
      // Check if it's a machines path and fetch machine name if needed
      if (pathnames[0] === "machines" && pathnames[1]) {
        try {
          const response = await getMachinesByIds({
            machines: [pathnames[1]],
            site_number: selectedSite,
            site_line_id: selectedSiteLineId,
          });
          if (response.status && Array.isArray(response.data) && response.data.length > 0) {
            setMachineName(response.data[0].machine_name);
          } else {
            setMachineName(pathnames[1]); // Revert to ID if failed
          }
        } catch (error) {
          console.error("Error fetching machine name:", error);
          setMachineName(pathnames[1]); // Revert to ID on error
        }
      }

      if (pathnames[0] === "documents" && pathnames[1]) {
        try {
          const response = await getMachinesByIds({
            machines: [pathnames[1]],
            site_number: selectedSite,
            site_line_id: selectedSiteLineId,
          });
          if (response.status && Array.isArray(response.data) && response.data.length > 0) {
            setMachineName(response.data[0].machine_name);
          } else {
            setMachineName(pathnames[1]); // Revert to ID if failed
          }
        } catch (error) {
          console.error("Error fetching machine name:", error);
          setMachineName(pathnames[1]); // Revert to ID on error
        }
      }

      // Check if it's a machines path and fetch product name if needed
      if (pathnames[0] === "machines" && pathnames[1] && pathnames[2]) {
        try {
          const response = await getSingleProduct({
            machine_id: pathnames[1],
            product_id: pathnames[2],
            site_number: selectedSite,
            site_line_id: selectedSiteLineId,
          });

          const resolvedProductName = getProductNameFromResponse(response);
          if (response.status && resolvedProductName) {
            setProductName(resolvedProductName);
          } else {
            setProductName(pathnames[2]); // Revert to ID if failed
          }
        } catch (error) {
          console.error("Error fetching product name:", error);
          setProductName(pathnames[2]); // Revert to ID on error
        }
      }

      if (pathnames[0] === "order-history" && (pathnames[1] || pathnames[2])) {
        try {
          const response = await getMachinesByIds({
            machines: [pathnames[1]],
            site_number: selectedSite,
            site_line_id: selectedSiteLineId,
          });
          if (response.status && Array.isArray(response.data) && response.data.length > 0) {
            setMachineName(response.data[0].machine_name);
          } else {
            setMachineName(pathnames[1]); // Revert to ID if failed
          }
          if (pathnames[2]) {
            const response1 = await getSingleProduct({
              machine_id: pathnames[1],
              product_id: pathnames[2],
              site_number: selectedSite,
              site_line_id: selectedSiteLineId,
            });

            const resolvedProductName = getProductNameFromResponse(response1);
            if (response1.status && resolvedProductName) {
              setProductName(resolvedProductName);
            } else {
              setProductName(pathnames[2]); // Revert to ID if failed
            }
          }
        } catch (error) {
          console.error("Error fetching product name:", error);
          setProductName(pathnames[2]); // Revert to ID on error
        }
      }

      // Check if it's an order path and fetch order name if needed
      if (pathnames[0] === "order-received" && pathnames[1]) {
        try {
          console.log('In BreadCrumb', pathnames)
          if (!pathnames[1] || pathnames[1] === "undefined") return;
          const response = await getOrderByKey({
            orderKey: pathnames[1],
          });
          console.log("In breadcrumbs, getOrderByKey called", response);
          if (response.status) {
            setOrderName(response.data.order.order_number);
          } else {
            setOrderName(pathnames[1]); // Revert to ID if failed
          }
        } catch (error) {
          console.error("Error fetching order name:", error);
          setOrderName(pathnames[1]); // Revert to ID on error
        }
      }
    };

    fetchData();
  }, [location.pathname, location.search, selectedSite]);

  function toCamelCase(str) {
    return str
      .split("_")
      .map(
        (word, index) =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join(" ");
  }

  const getDisplayValue = (value, index, machineMode = "default") => {
    let displayValue = value;

    if (pathnames[0] === "machines" && index === 0) {
      if (machineMode === "lines") {
        displayValue = "My Lines";
      } else {
        displayValue = "Machines";
      }
    } else if (pathnames[0] === "machines" && index === 1 && machineName) {
      displayValue = machineName;
    } else if (pathnames[0] === "order-history" && index === 1 && machineName) {
      displayValue = machineName;
    } else if (pathnames[0] === "documents" && index === 1 && machineName) {
      displayValue = machineName;
    } else if (pathnames[0] === "machines" && index === 2 && productName) {
      displayValue = productName;
    } else if (pathnames[0] === "order-history" && index === 2 && productName) {
      displayValue = productName;
    } else if (pathnames[0] === "order-received" && index === 1 && orderName) {
      displayValue = orderName;
    } else if (pathnames[0] === "saved_for_later") {
      const transformedPathnames = toCamelCase(pathnames[0]);
      displayValue = transformedPathnames;
	    } else if (pathnames[0] === "order-history" && index === 0) {
	      displayValue = "Quote history";
	    } else if (pathnames[0] === "order-received" && index === 0) {
	      displayValue = "Quote Received";
	    } else if (pathnames[0] === "cart" && index === 0) {
	      displayValue = "Quote";
	    } else if (pathnames[0] === "checkout" && index === 0) {
      displayValue = "Quote Received";
    }

    if (displayValue.toLowerCase() === "recommendations") {
      displayValue = t("LABELS.MY_LISTS", { defaultValue: "My Lists" });
    }

    if (DEFAULT_LAYOUT === "layout2") {
      if (displayValue.toLowerCase() === "order history" || displayValue.toLowerCase() === "order-history") {
        displayValue = t('BUTTON.BUY_AGAIN');
      }
      if (displayValue.toLowerCase() === "requests") {
        displayValue = t("LABELS.CUSTOMER_CARE_REQUESTS");
      }
    } else if (displayValue.toLowerCase() === "requests") {
      displayValue = t("LABELS.REQUEST");
    }

    return displayValue;
  };

  const formatDisplayValue = (displayValue) => {
    if (!displayValue) return displayValue;

    // Capitalize each breadcrumb item
    // Special case for RSPL
    let capitalizedValue;
    if (displayValue.toLowerCase() === "rspl") {
      capitalizedValue = DEFAULT_LAYOUT === "layout2"
        ? t("LABELS.MY_RSPL", { defaultValue: "My RSPL" })
        : "Recommended Spare Parts Lists (RSPL)";
    } else {
      // Default capitalization for other breadcrumbs
      capitalizedValue =
        displayValue.charAt(0).toUpperCase() +
        displayValue.slice(1).replace(/-/g, " ");
    }

    return capitalizedValue;
  };

  const breadcrumbLinks = (() => {
    if (pathnames[0] === "machines") {
      const items = [];

      if (selectedSiteLineId) {
        items.push({
          key: "machines-lines",
          to: ROUTES.MACHINES,
          label: formatDisplayValue("My Lines"),
        });
        if (selectedLineName) {
          items.push({
            key: "machines-line-name",
            to: `${ROUTES.MACHINES}${machineLineQuery}`,
            label: selectedLineName.charAt(0).toUpperCase() + selectedLineName.slice(1),
          });
        }
      }

      pathnames.forEach((value, index) => {
        const machineMode = !selectedSiteLineId && pathnames.length === 1 && index === 0 ? "lines" : "default";
        const displayValue = getDisplayValue(value, index, machineMode);
        items.push({
          key: `machines-${index}-${value}`,
          to: `/${pathnames.slice(0, index + 1).join("/")}${machineLineQuery}`,
          label: formatDisplayValue(displayValue),
        });
      });

      return items;
    }

    if (
      selectedSiteLineId &&
      (pathnames[0] === "documents" || pathnames[0] === "order-history")
    ) {
      const items = [
        {
          key: `${pathnames[0]}-lines`,
          to: ROUTES.MACHINES,
          label: formatDisplayValue("My Lines"),
        },
      ];
      if (selectedLineName) {
        items.push({
          key: `${pathnames[0]}-line-name`,
          to: `${ROUTES.MACHINES}${machineLineQuery}`,
          label: selectedLineName.charAt(0).toUpperCase() + selectedLineName.slice(1),
        });
      }
      items.push({
        key: `${pathnames[0]}-machines`,
        to: `${ROUTES.MACHINES}${machineLineQuery}`,
        label: formatDisplayValue("Machines"),
      });

      pathnames
        .filter((value) => value.toLowerCase() !== "home")
        .forEach((value, index) => {
          const to = `/${pathnames.slice(0, index + 1).join("/")}${machineLineQuery}`;
          const displayValue = getDisplayValue(value, index);

          items.push({
            key: `${to}-line-context`,
            to,
            label: formatDisplayValue(displayValue),
          });
        });

      return items;
    }

    return pathnames.filter((value) => value.toLowerCase() !== "home").map((value, index) => {
      const isOrderRoute = pathnames[0] === "order-received";
      const to = (() => {
        if (isOrderRoute && pathnames.length > 1) {
          const upto = Math.min(index + 2, pathnames.length);
          return `/${pathnames.slice(0, upto).join("/")}`;
        }
        return `/${pathnames.slice(0, index + 1).join("/")}`;
      })();
      const displayValue = getDisplayValue(value, index);

      return {
        key: to,
        to,
        label: formatDisplayValue(displayValue),
      };
    });
  })();

  const breadcrumbItems = breadcrumbLinks.map(({ key, to, label }) => {
    return (
      <Link key={key} to={to}>
        <Typography color="black" fontSize="12px">
          {label}
        </Typography>
      </Link>
    );
  });

  const needsPartsPrefix = (() => {
    if (DEFAULT_LAYOUT !== "layout2") return false;
    const first = (pathnames[0] || "").toLowerCase();
    return (
      first === "search-results" ||
      first === "machines" ||
      first === "order-history" ||
      first === "buy-again" ||
      first === "rspl" ||
      first === "recommendations" ||
      first === "requests" ||
      first === "saved_for_later" ||
      first === "track-quote"
    );
  })();

  return (
    <Breadcrumbs
      separator={<NavigateNextOutlined fontSize="small" />}
      aria-label="breadcrumb"
      style={{ marginTop: "10px", padding: "0 30px" }}
    >
      <Link to={ROUTES.HOME_ALIA}>
        <Typography color="primary" fontSize="12px">
          {t("HOME")}
        </Typography>
      </Link>
      {needsPartsPrefix && (
        <Link to="/parts">
          <Typography color="black" fontSize="12px">
            {t("LABELS.PARTS", { defaultValue: "Parts" })}
          </Typography>
        </Link>
      )}
      {breadcrumbItems}
    </Breadcrumbs>
  );
};

export default BreadCrumbs;
